import { Status, TaskItemDTO } from '@src/services/brain/modules/v1/tasks/dto/brain-tasks.get.response.dto';
import { BrainApi } from '../../../services/brain/brain-api';
import { TTaskItem, TTaskSource, TTaskType } from './dto/taskItem.dto';
import { TasksItemMapper } from './tasks.mapper';
import { GetByIdTaskDTO } from '@src/services/brain/modules/v1/tasks/dto/brain-tasks.getById.response.dto';
import { GetTasksSummaryResponseDTO } from '@src/services/brain/modules/v1/tasks/dto/brain-tasks.getSummary.response.dto';
import Cookies from 'js-cookie';
import { TaskCommentDTO } from '@src/services/brain/modules/v1/tasks/dto/brain-tasks-comments.response.dto';
import { GetTasksGroupsResponseDTO } from '@src/services/brain/modules/v1/tasks/dto/brain-tasks.getGroups.response.dto';
import { RequestGetByIdResponseDTO } from '@src/services/automations/modules/v1/requests/dto/requests.getById.response.dto';
import { AutomationsApi } from '@src/services/automations/automations-api';
import {
  GetTasksAnalyticsResponseDTO,
  TAnalyticsGranularity,
} from '@src/services/brain/modules/v1/tasks/dto/brain-tasks.getAnalytics.response.dto';

const client = new BrainApi();

export interface TaskFilters {
  startDate?: Date | null;
  endDate?: Date | null;
  updateStartDate?: Date | null;
  updateEndDate?: Date | null;
  status?: string[];
  source?: string[];
  type?: TTaskType[];
  assignedUserId?: string | null;
  message?: string;
  organizationId?: string;
  sortBy?: 'createdAt' | 'completedAt';
  sortOrder?: 'asc' | 'desc';
}

const generateParams = (filters: TaskFilters, limit: number = 100) => {
  const params: Record<string, unknown> = {
    limit,
  };

  if (filters.startDate) {
    params.startDate = filters.startDate.toISOString();
  }
  if (filters.endDate) {
    params.endDate = filters.endDate.toISOString();
  }
  if (filters.updateStartDate) {
    params.updateStartDate = filters.updateStartDate.toISOString();
  }
  if (filters.updateEndDate) {
    params.updateEndDate = filters.updateEndDate.toISOString();
  }
  if (filters.status && filters.status.length > 0) {
    params.status = filters.status;
  }
  if (filters.source && filters.source.length > 0) {
    params.source = filters.source;
  }
  if (filters.type && filters.type.length > 0) {
    params.type = filters.type;
  }
  if (filters.assignedUserId !== undefined) {
    if (filters.assignedUserId !== 'me_or_unassigned') {
      params.assignedUserId = filters.assignedUserId;
    }
  }
  if (filters.message) {
    params.message = filters.message;
  }
  if (filters.organizationId) {
    params.organizationId = filters.organizationId;
  }
  if (filters.sortBy) {
    params.sortBy = filters.sortBy;
  }
  if (filters.sortOrder) {
    params.sortOrder = filters.sortOrder;
  }

  return params;
};

export const getTasks = async (
  filters: TaskFilters = {},
  specificPage: number,
): Promise<{ items: TTaskItem[]; totalPages: number; currentPage: number }> => {
  const params = generateParams(filters);

  try {
    const response = await client.v1.tasks.get({
      params: {
        ...params,
        currentPage: specificPage,
      },
    });

    let filteredItems = response.items;

    if (filters.assignedUserId === 'me_or_unassigned') {
      const currentUserId = Cookies.get('userId');
      if (currentUserId) {
        filteredItems = response.items.filter((task) => task.assignedUserId === currentUserId || !task.assignedUserId);
      }
    }

    return {
      items: filteredItems.map((task) => TasksItemMapper(task)),
      totalPages: response.totalPages,
      currentPage: response.currentPage,
    };
  } catch {
    return {
      items: [],
      totalPages: 1,
      currentPage: 1,
    };
  }
};

export const getTasksGroups = async (
  filters: TaskFilters = {},
  currentPage: number = 1,
): Promise<GetTasksGroupsResponseDTO> => {
  const params = generateParams(filters, 20);
  return await client.v1.tasks.getGroups({
    params: {
      ...params,
      currentPage,
    },
  });
};

export const getTaskById = async (taskId: string): Promise<GetByIdTaskDTO> => {
  return await client.v1.tasks.getById({
    id: taskId,
  });
};

export const updateTaskStatus = async (taskId: string, status: Status): Promise<TaskItemDTO> => {
  return await client.v1.tasks.putById({
    id: taskId,
    body: {
      status,
    },
  });
};

export const updateTaskAssignedUser = async (taskId: string, userId: string | null): Promise<TaskItemDTO> => {
  return await client.v1.tasks.putById({
    id: taskId,
    body: {
      assignedUserId: userId ?? undefined,
    },
  });
};

export const getTasksSummary = async (filters: TaskFilters = {}): Promise<GetTasksSummaryResponseDTO> => {
  const params = generateParams(filters);

  return await client.v1.tasks.getSummary({
    params,
  });
};

export const updateBulkTasks = async (taskIds: string[], status: Status, assignedUserId?: string): Promise<void> => {
  await client.v1.tasks.putBulk({
    body: {
      taskIds: taskIds,
      status,
      assignedUserId,
    },
  });
};

export const createTaskComment = async (taskId: string, content: string): Promise<TaskCommentDTO> => {
  return await client.v1.tasks.postComment({
    taskId,
    body: {
      content,
    },
  });
};

export const updateTaskComment = async (
  taskId: string,
  commentId: string,
  content: string,
): Promise<TaskCommentDTO> => {
  return await client.v1.tasks.putComment({
    taskId,
    commentId,
    body: {
      content,
    },
  });
};

export const deleteTaskComment = async (taskId: string, commentId: string): Promise<void> => {
  await client.v1.tasks.deleteComment({
    taskId,
    commentId,
  });
};

export const getTaskContext = async (
  requestId?: string,
  taskSource?: TTaskSource,
): Promise<RequestGetByIdResponseDTO | undefined> => {
  if (!requestId || taskSource !== 'AUTOMATIONS') {
    return undefined;
  }
  const automationsClient = new AutomationsApi();
  const result = await automationsClient.v1.requests.getById({
    id: requestId,
  });
  return result;
};

export const createTaskRerun = async (taskId: string): Promise<{ message: string }> => {
  return await client.v1.tasks.postRerun({
    id: taskId,
  });
};

export const getTasksAnalytics = async (
  filters: TaskFilters = {},
  granularity: TAnalyticsGranularity = 'day',
): Promise<GetTasksAnalyticsResponseDTO> => {
  return await client.v1.tasks.getAnalytics({
    params: {
      startDate: filters.startDate?.toISOString(),
      endDate: filters.endDate?.toISOString(),
      granularity,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      source: filters.source && filters.source.length > 0 ? filters.source : undefined,
      type: filters.type && filters.type.length > 0 ? filters.type : undefined,
      organizationId: filters.organizationId || undefined,
    },
  });
};
